import { ISelectItemGroup } from "~/components/Controls/Select/MultiSelectPopup";
import { PresetSide } from "~/modules/Dictionary/models/Preset";

export interface IRillReportFields {
  report: string;
  fields: string[];
}

// Fields a preset can grant, per side and report; mirrors rill-dashboards external/<side>/metrics.
const SUPPLY_REPORT_FIELDS: IRillReportFields[] = [
  {
    report: "ad-requests",
    fields: [
      "__time",
      "placementTag",
      "platform",
      "region",
      "source_id",
      "source_name",
      "mediation_stack_id",
      "test",
      "count",
      "uniqueDevices",
    ],
  },
  {
    report: "clicks",
    fields: [
      "__time",
      "region",
      "country",
      "agency_name",
      "source_id",
      "source_name",
      "mediation_stack_id",
      "mediation_mode",
      "device_os",
      "device_os_ver",
      "device_type",
      "is_zero_ifa",
      "is_gpdr",
      "is_ad_network",
      "ad_network_placement_id",
      "ad_type",
      "ad_placement_tag",
      "video_placement_type",
      "ad_size",
      "ad_domain",
      "cid",
      "crid",
      "ad_bundle",
      "bundle_key",
      "bundle_raw_id",
      "bundle_name",
      "bundle_ver",
      "bundle_publisher_id",
      "impression_tag",
      "exchange_placement_id",
      "exchange_placement_name",
      "seller_name",
      "click_cnt",
    ],
  },
  {
    report: "dau",
    fields: [
      "__time",
      "country",
      "source_id",
      "ssp_name",
      "source_name",
      "device_os",
      "is_zero_ifa",
      "bundle",
      "req_dau_us_cnt",
    ],
  },
  {
    report: "impressions",
    fields: [
      "__time",
      "region",
      "country",
      "agency_name",
      "demand_partner",
      "is_paas",
      "source_id",
      "source_name",
      "mediation_stack_id",
      "mediation_mode",
      "payer_data",
      "device_os",
      "device_os_ver",
      "device_connection_type",
      "is_zero_ifa",
      "device_type",
      "is_gpdr",
      "is_coppa",
      "is_ad_network",
      "ad_network_placement_id",
      "ad_type",
      "ad_type_bi",
      "ad_placement_tag",
      "video_placement_type",
      "ad_size",
      "ad_domain",
      "cid",
      "crid",
      "ad_bundle",
      "iab_category",
      "bidder_iab_category",
      "bundle_key",
      "bundle_raw_id",
      "bundle_name",
      "bundle_ver",
      "bundle_publisher_id",
      "impression_tag",
      "exchange_placement_id",
      "exchange_placement_name",
      "dcid",
      "test",
      "impression_cnt",
      "seller_income_sum",
      "seller_income_ecpm",
      "device_count",
    ],
  },
  {
    report: "revenue-report",
    fields: [
      "__time",
      "agency_id",
      "agency_name",
      "demand_partner",
      "is_paas",
      "payer_data",
      "dsp_api_spend_sum",
      "dsp_api_seller_income_sum",
      "bidmachine_fee_sum",
      "expected_payment_sum",
    ],
  },
];

// Ad Requests and DAU are supply-only.
const DEMAND_REPORT_FIELDS: IRillReportFields[] = [
  {
    report: "clicks",
    fields: [
      "__time",
      "region",
      "country",
      "agency_name",
      "seller_name",
      "source_id",
      "mediation_stack_id",
      "mediation_mode",
      "device_os",
      "device_os_ver",
      "device_type",
      "is_zero_ifa",
      "is_gpdr",
      "ad_placement_tag",
      "video_placement_type",
      "ad_size",
      "ad_domain",
      "cid",
      "crid",
      "ad_bundle",
      "bundle_name",
      "bundle_ver",
      "impression_tag",
      "exchange_placement_id",
      "click_cnt",
    ],
  },
  {
    report: "impressions",
    fields: [
      "__time",
      "region",
      "country",
      "agency_name",
      "demand_partner",
      "is_paas",
      "seller_name",
      "source_id",
      "mediation_stack_id",
      "mediation_mode",
      "payer_data",
      "device_os",
      "device_os_ver",
      "device_connection_type",
      "is_zero_ifa",
      "device_type",
      "is_gpdr",
      "is_coppa",
      "ad_type_bi",
      "ad_placement_tag",
      "video_placement_type",
      "ad_size",
      "ad_domain",
      "cid",
      "crid",
      "ad_bundle",
      "iab_category",
      "bidder_iab_category",
      "bundle_name",
      "bundle_ver",
      "impression_tag",
      "exchange_placement_id",
      "impression_cnt",
      "spend_sum",
      "spend_ecpm",
      "seller_income_sum",
      "seller_income_ecpm",
    ],
  },
  {
    report: "revenue-report",
    fields: [
      "__time",
      "agency_id",
      "agency_name",
      "demand_partner",
      "is_paas",
      "payer_data",
      "dsp_api_spend_sum",
      "dsp_api_seller_income_sum",
      "bidmachine_fee_sum",
      "expected_payment_sum",
      "dsp_api_impression_cnt",
    ],
  },
];

export const RILL_REPORT_FIELDS: Record<PresetSide, IRillReportFields[]> = {
  supply: SUPPLY_REPORT_FIELDS,
  demand: DEMAND_REPORT_FIELDS,
};

export const qualifyRillFieldId = (report: string, field: string): string => `${report}:${field}`;

export const parseRillFieldId = (fieldId: string): { report: string; field: string } | null => {
  const separatorIndex = fieldId.indexOf(":");
  if (separatorIndex <= 0 || separatorIndex === fieldId.length - 1) {
    return null;
  }
  return { report: fieldId.slice(0, separatorIndex), field: fieldId.slice(separatorIndex + 1) };
};

export const formatRillFieldId = (fieldId: string): string => {
  const parsed = parseRillFieldId(fieldId);
  return parsed ? `${parsed.field} (${parsed.report})` : fieldId;
};

const toFieldGroups = (reports: IRillReportFields[]): ISelectItemGroup[] =>
  reports.map(({ report, fields }) => ({
    label: report,
    items: fields.map((field) => ({ label: field, value: qualifyRillFieldId(report, field) })),
  }));

export const RILL_FIELD_GROUPS: Record<PresetSide, ISelectItemGroup[]> = {
  supply: toFieldGroups(RILL_REPORT_FIELDS.supply),
  demand: toFieldGroups(RILL_REPORT_FIELDS.demand),
};

export interface IGroupedRillFieldIds {
  report: string;
  fields: string[];
}

export const groupRillFieldIds = (fieldIds: string[]): IGroupedRillFieldIds[] => {
  const fieldsByReport = new Map<string, string[]>();
  fieldIds.forEach((fieldId) => {
    const parsed = parseRillFieldId(fieldId);
    const report = parsed?.report ?? "Other";
    const field = parsed?.field ?? fieldId;
    fieldsByReport.set(report, (fieldsByReport.get(report) ?? []).concat(field));
  });
  return Array.from(fieldsByReport.entries()).map(([report, fields]) => ({ report, fields }));
};
